import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { TranslateService } from '@ngx-translate/core';
import { AccessControl } from '@pncl/common-models';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { SUBSCRIPTION_TYPES } from '../models/payment';
import { SpaceRepository } from '../state/space.repository';
import { CapabilityTierService } from './capability-tier.service';
import { UserService } from './user.service';
import { AnalyticsDashboardV2Variable, FLAGS, FlagsService } from './flags.service';
import Capability = AccessControl.Capability;

/** The two capabilities gating the download affordances, which move independently. */
export interface DownloadCapabilities {
  /** DOWNLOAD_CLOUD_RECORDINGS: gates downloading the recording itself */
  recording: boolean;
  /** CLOUD_RECORDING: gates downloading the transcript */
  transcript: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureManagementService {
  // Static pre-translated messages for feature unavailability
  public readonly featureUnavailableTooltip = {
    title: this.translateService.instant('Feature Not Available'),
    message: this.translateService.instant('This feature is not part of your plan.'),
  };

  public currentSpaceBelongToFreeGuestUser$ = this.checkSpaceOwnerSubscription$(
    SUBSCRIPTION_TYPES.FREE,
  );

  private webViewerEnabledSubject = new BehaviorSubject<boolean>(false);
  public isWebViewerEnabled$: Observable<boolean> = this.webViewerEnabledSubject.asObservable();

  public readonly isCloudRecordingCapabilityEnabled$: Observable<boolean>;
  public readonly isDownloadCloudRecordingsEnabled$: Observable<boolean>;
  public readonly downloadCapabilities$: Observable<DownloadCapabilities>;
  public readonly displayCloudRecording$: Observable<boolean>;
  public readonly isAutomaticRecordingCapabilityEnabled$: Observable<boolean>;
  public readonly isInstitutionTemplatesEnabled$: Observable<boolean>;
  public readonly childModeCapabilityEnabled$: Observable<boolean>;

  private isAiCoachInsightsEnabledSubject = new BehaviorSubject<boolean>(false);
  public readonly isAiCoachInsightsEnabled$ = this.isAiCoachInsightsEnabledSubject.asObservable();

  private isTeachersCoachingReportEnabledSubject = new BehaviorSubject<boolean>(false);
  public readonly isTeachersCoachingReportEnabled$ =
    this.isTeachersCoachingReportEnabledSubject.asObservable();

  public readonly isStartCallEnabled$: Observable<boolean>;
  public readonly isRaiseHandEnabled$: Observable<boolean>;
  public readonly isExportBoardsAsImageEnabled$: Observable<boolean>;
  public readonly isExportImportSpaceFileEnabled$: Observable<boolean>;

  private isAiSnippetsEnabledSubject = new BehaviorSubject<boolean>(false);
  public readonly isAiSnippetsEnabled$ = this.isAiSnippetsEnabledSubject.asObservable();

  constructor(
    private capabilityTierService: CapabilityTierService,
    private spaceRepo: SpaceRepository,
    protected userService: UserService,
    private translateService: TranslateService,
    private flagsService: FlagsService,
  ) {
    this.capabilityTierService
      .shouldEnableCapability$(Capability.WEB_VIEWER)
      .subscribe((webViewerCapabilityEnabled) => {
        this.webViewerEnabledSubject.next(webViewerCapabilityEnabled);
      });

    this.isStartCallEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.START_CALL)
      .pipe(distinctUntilChanged());

    this.isRaiseHandEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.RAISE_HAND)
      .pipe(distinctUntilChanged());

    this.isExportBoardsAsImageEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.EXPORT_BOARDS_AS_IMAGE)
      .pipe(distinctUntilChanged());

    this.isExportImportSpaceFileEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.EXPORT_IMPORT_SPACE_FILE)
      .pipe(distinctUntilChanged());

    this.isCloudRecordingCapabilityEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.CLOUD_RECORDING)
      .pipe(distinctUntilChanged());

    this.isDownloadCloudRecordingsEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.DOWNLOAD_CLOUD_RECORDINGS)
      .pipe(distinctUntilChanged());

    this.downloadCapabilities$ = combineLatest([
      this.isDownloadCloudRecordingsEnabled$,
      this.isCloudRecordingCapabilityEnabled$,
    ]).pipe(map(([recording, transcript]) => ({ recording, transcript })));

    this.displayCloudRecording$ = this.capabilityTierService
      .shouldDisplayCapability$(Capability.CLOUD_RECORDING)
      .pipe(distinctUntilChanged());

    // Initialize the new automatic recording capability observable
    this.isAutomaticRecordingCapabilityEnabled$ =
      this.capabilityTierService.shouldEnableCapability$(Capability.AUTOMATIC_CLOUD_RECORDING);

    // Initialize the institution templates capability observable
    this.isInstitutionTemplatesEnabled$ = this.capabilityTierService.shouldEnableCapability$(
      Capability.INSTITUTION_TEMPLATES,
    );

    this.childModeCapabilityEnabled$ = this.capabilityTierService
      .shouldEnableCapability$(Capability.CHILD_MODE)
      .pipe(distinctUntilChanged());

    combineLatest([
      this.capabilityTierService.shouldEnableCapability$(Capability.AI_COACH),
      this.userService.user.pipe(filterNil()),
      this.flagsService.featureFlagChanged(FLAGS.AI_RECORDING_INSIGHT_DASHBOARD),
    ])
      .pipe(
        map(
          ([isCapabilityEnabled, userRes, isFlagEnabled]) =>
            isCapabilityEnabled &&
            isFlagEnabled &&
            (userRes.user?.institution?.settings?.aiCoachingInsights ?? false),
        ),
        distinctUntilChanged(),
      )
      .subscribe((enabled) => this.isAiCoachInsightsEnabledSubject.next(enabled));

    combineLatest([
      this.userService.user.pipe(filterNil()),
      this.flagsService.featureFlagChanged(FLAGS.ANALYTICS_DASHBOARDS_V2, true),
    ])
      .pipe(
        map(() => this.evaluateTeachersCoachingReportEnabled()),
        distinctUntilChanged(),
      )
      .subscribe((enabled) => this.isTeachersCoachingReportEnabledSubject.next(enabled));

    combineLatest([
      this.capabilityTierService.shouldEnableCapability$(Capability.AI_SNIPPETS),
      this.userService.user.pipe(filterNil()),
    ])
      .pipe(
        map(
          ([isCapabilityEnabled, userRes]) =>
            isCapabilityEnabled && (userRes.user?.institution?.settings?.aiSnippets ?? false),
        ),
        distinctUntilChanged(),
      )
      .subscribe((enabled) => this.isAiSnippetsEnabledSubject.next(enabled));
  }

  // WEB VIEWER WRAPPER FUNCTIONS

  shouldDisplayWebViewer$(): Observable<boolean> {
    // Display webviewers for guest users and institutions with tiers assigned only
    return this.capabilityTierService
      .shouldDisplayCapability$(Capability.WEB_VIEWER)
      .pipe(
        map(
          (shouldDisplayWebViewer) => shouldDisplayWebViewer || this.showWebViewerForGuestUsers(),
        ),
      );
  }

  shouldDisplayWebViewer(): boolean {
    return (
      this.capabilityTierService.shouldDisplayCapability(Capability.WEB_VIEWER) ||
      this.showWebViewerForGuestUsers()
    );
  }

  isWebViewerEnabled(): boolean {
    return this.webViewerEnabledSubject.getValue();
  }

  isStartCallEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.START_CALL);
  }

  isRaiseHandEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.RAISE_HAND);
  }

  public currentSpaceBelongsToInstitution$ = this.spaceRepo.activeSpaceId$.pipe(
    filterNil(),
    map((spaceId) => {
      const space = this.spaceRepo.getSpace(spaceId);
      return !!space?.institution;
    }),
  );

  private showWebViewerForGuestUsers(): boolean {
    // show web viewer app and icon to guest users
    // hide web viewers for institutions
    return !this.spaceRepo.activeSpace?.institution;
  }

  private checkSpaceOwnerSubscription$(
    subscriptions: SUBSCRIPTION_TYPES | SUBSCRIPTION_TYPES[],
  ): Observable<boolean> {
    return this.spaceRepo.spaceOwnerSubscriptionType$.pipe(
      map((spaceOwnerSubscriptionType) =>
        spaceOwnerSubscriptionType
          ? [subscriptions].flat().includes(spaceOwnerSubscriptionType)
          : false,
      ),
    );
  }

  isAiCoachInsightsEnabled(): boolean {
    return this.isAiCoachInsightsEnabledSubject.getValue();
  }

  isTeachersCoachingReportEnabled(): boolean {
    return this.isTeachersCoachingReportEnabledSubject.getValue();
  }

  private evaluateTeachersCoachingReportEnabled(): boolean {
    return this.flagsService.isAnalyticsDashboardV2Enabled(
      AnalyticsDashboardV2Variable.TeachersCoaching,
    );
  }

  isAiSnippetsEnabled(): boolean {
    return this.isAiSnippetsEnabledSubject.getValue();
  }

  isPencilFilesEnabled() {
    return this.capabilityTierService.shouldEnableCapability(Capability.PENCIL_FILES);
  }

  // LMS CAPABILITY WRAPPER FUNCTIONS

  isLmsEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.LMS);
  }

  // CUSTOM_ATTRIBUTES CAPABILITY WRAPPER FUNCTIONS

  isCustomAttributesEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.CUSTOM_ATTRIBUTES);
  }

  isSchedulingEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.SCHEDULING);
  }

  isAvailabilityEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.AVAILABILITY);
  }

  // MESSAGING CAPABILITY WRAPPER FUNCTIONS

  isGlobalMessagingEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.GLOBAL_MESSAGING);
  }

  isControlCenterAlertsEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.CONTROL_CENTER_ALERTS);
  }

  isExportBoardsAsImageEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.EXPORT_BOARDS_AS_IMAGE);
  }

  isExportImportSpaceFileEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.EXPORT_IMPORT_SPACE_FILE);
  }

  isDownloadCloudRecordingsEnabled(): boolean {
    return this.capabilityTierService.shouldEnableCapability(Capability.DOWNLOAD_CLOUD_RECORDINGS);
  }
}
